import { Link } from 'react-router-dom';
import { X, User, LogOut } from 'lucide-react';
import { SocialLinks } from './SocialLinks';
import { NAV_LINKS } from './types';
import toast from 'react-hot-toast';

interface MobileMenuProps {
  isOpen: boolean;
  isActive: (path: string) => boolean;
  user: any;
  signOut: () => Promise<void>;
  onToggle: () => void;
}

/** Mobile full-screen overlay menu with animated links */
export function MobileMenu({ isOpen, isActive, user, signOut, onToggle }: MobileMenuProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto md:hidden animate-fadeIn" style={{ animationDuration: '300ms' }}>
      <div className="p-4 border-b flex justify-between items-center">
        <Link to="/" className="flex items-center" onClick={onToggle}>
          <img
            src="/JME_fit_black_purple.png"
            alt="JmeFit Training"
            className="h-10 w-auto"
          />
        </Link>
        <button
          onClick={onToggle}
          className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-all duration-300 hover:rotate-90"
          aria-label="Close menu"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="p-4 space-y-4 animate-slideDown" style={{ animationDuration: '400ms' }}>
        {NAV_LINKS.map((link, index) => (
          <Link
            key={link.to}
            to={link.to}
            onClick={onToggle}
            className={`block px-4 py-3 rounded-lg text-lg font-medium transition-all duration-300 ${isActive(link.to) ? 'bg-gradient-to-r from-jme-purple to-pink-500 text-white shadow-md' : 'text-gray-900 hover:bg-gray-100 hover:translate-x-2'}`}
            style={{ animationDelay: `${(index + 1) * 50}ms`, opacity: 0, animation: `fadeIn 0.5s ease forwards ${(index + 1) * 50}ms` }}
          >
            <span className="relative inline-block transition-transform duration-300 group-hover:translate-y-[-1px]">{link.label}</span>
          </Link>
        ))}
      </div>

      <div className="p-4 border-t animate-fadeIn" style={{ animationDelay: '400ms', opacity: 0, animation: 'fadeIn 0.5s ease forwards 400ms' }}>
        {user ? (
          <div className="space-y-3">
            <div className="flex items-center space-x-2 px-4 py-3 rounded-lg bg-gray-50">
              <User className="w-5 h-5 text-gray-600" />
              <span className="text-base font-medium text-gray-900">
                {user.user_metadata.full_name || 'Account'}
              </span>
            </div>
            <Link
              to="/dashboard"
              onClick={onToggle}
              className="block w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-300 flex items-center hover:translate-x-2"
            >
              <User className="w-4 h-4 inline mr-2" />
              Dashboard
            </Link>
            <button
              onClick={async () => {
                await signOut();
                onToggle();
                toast.success('Signed out successfully');
              }}
              className="block w-full px-4 py-3 text-left text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300 flex items-center hover:translate-x-2"
            >
              <LogOut className="w-4 h-4 inline mr-2" />
              Sign Out
            </button>
          </div>
        ) : (
          <div className="flex flex-col space-y-3">
            <Link
              to="/auth"
              onClick={onToggle}
              className="block w-full px-4 py-3 text-center text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-300 font-medium hover:shadow-md"
            >
              Sign In
            </Link>
            <Link
              to="/auth?mode=signup"
              onClick={onToggle}
              className="block w-full px-4 py-3 text-center text-white bg-gradient-to-r from-jme-purple to-pink-600 hover:from-pink-600 hover:to-jme-purple rounded-lg transition-all shadow-md font-medium btn-hover-effect"
            >
              Get Started
            </Link>
          </div>
        )}
      </div>

      <div className="p-4 border-t flex justify-center space-x-6 animate-fadeIn" style={{ animationDelay: '450ms' }}>
        <SocialLinks size="md" />
      </div>
    </div>
  );
}
